import { useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import BodySmall from "../../atoms/typography/BodySmall";
import Tooltip from "../Tooltip";
import ToggleButton from "../../atoms/buttons/ToggleButton";
import { theme } from "../../../theme";
import { CrossIcon } from "../../atoms/icons/CrossIcon";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import { HorizontalSpacer } from "../../atoms/Spacers";
import DetailsButton from "../../atoms/buttons/DetailsButton";
import { Row } from "react-grid-system";
import {
  ChevronSmallDownIcon,
  ChevronSmallUpIcon,
} from "../../atoms/icons/ChevronIcons";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import Small from "../../atoms/typography/Small";
import { useSetLgStatusOnInvoice } from "../../../graphql/hooks/invoices/useSetLgStatusOnInvoice";
import Modal from "../Modal";
import Body from "../../atoms/typography/Body";
import TextField from "../../atoms/inputs/TextField";
import Alert from "../Alert";
import { useInvoiceDeliveryApprovals } from "../../../graphql/hooks/invoices/useInvoiceDeliveryApprovals";
import SmallLoadingSpinner from "../../atoms/SmallLoadingSpinner";
import dayjs from "dayjs";
import type {
  InvoiceRecipientApprovalModel,
  ReciepientApprovalStatus,
} from "../../../models/InvoiceModels";

interface Props {
  invoiceId: string;
  clientIdentifier: string;
  disabled?: boolean;
}

export const LgHistoryDetailBlock: FC<{
  lg: InvoiceRecipientApprovalModel;
}> = ({ lg }) => {
  const { t } = useTranslation();
  return (
    <div>
      <div style={{ padding: 4, backgroundColor: theme.colors.bgSubtle }}>
        <Small color={theme.colors.textSecondary}>
          {dayjs(lg.sentAt).format("YYYY-MM-DD HH:mm")}
        </Small>
        <BodySmall>
          {t("invoices.deliveryApprovalSentToBy", {
            recipient: lg.recipientEmailAddress,
            sender: lg.sentBySkynetUserId,
          })}
        </BodySmall>
      </div>
      {lg?.comments?.items?.map((comment) => (
        <div
          style={{
            padding: 4,
            backgroundColor: theme.colors.bgSubtle,
          }}
          key={comment.id}
        >
          <Small color={theme.colors.textSecondary}>
            {dayjs(comment?.createdAt).format("YYYY-MM-DD HH:mm")}{" "}
            {comment?.createdByUser?.firstName}{" "}
            {comment?.createdByUser?.lastName}
          </Small>
          <BodySmall>{comment?.content}</BodySmall>
        </div>
      ))}
      <HorizontalSpacer spacing={1} />
    </div>
  );
};

const DeliverApprovalStatusOnInvoice: React.FC<Props> = ({
  invoiceId,
  clientIdentifier,
  disabled,
}) => {
  const { t } = useTranslation();

  const [lgHistoryOpenIds, setLgHistoryOpenIds] = useState<Array<string>>([]);

  const [success, setSuccess] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [selectedStatus, setSelectedStatus] =
    useState<ReciepientApprovalStatus>("UNKNOWN");
  const [selectedLgId, setSelectedLgId] = useState<string | null>(null);
  const [commentModalOpen, setCommentModalOpen] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  const {
    invoice,
    error: lgError,
    loading: lgLoading,
  } = useInvoiceDeliveryApprovals(clientIdentifier, invoiceId);

  const onCompleted = (result?: any) => {
    if (result?.invoice?.setLgStatusOnInvoice?.success) {
      setSuccess(true);
      setErrorMessage(null);
      setComment("");
      setSelectedLgId(null);
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } else if (result?.invoice?.setLgStatusOnInvoice?.errorMessage) {
      setSuccess(false);
      setErrorMessage(result.invoice.setLgStatusOnInvoice.errorMessage);
    } else {
      console.error(
        "Unknown error setting delivery approval status on invoice",
      );
      setSuccess(false);
      setErrorMessage("Unknown error");
    }
  };
  const onError = (e?: any) => {
    setSuccess(false);
    setErrorMessage(e?.toString() || "Unknown error");
  };
  const { setLgStatusOnInvoice, loading, error } = useSetLgStatusOnInvoice({
    onCompleted,
    onError,
  });

  const onUpdateStatus = async () => {
    try {
      await setLgStatusOnInvoice({
        variables: {
          input: {
            comment: comment,
            invoiceId: invoiceId,
            lgStatus: selectedStatus,
            invoiceRecipientApprovalId: selectedLgId,
          },
        },
      });
    } catch (error) {
      console.error("Error updating LG status:", error);
      setErrorMessage("Error updating LG status: " + error);
    }
  };

  const onClickToggleLgHistory = (lgId: string) => {
    if (lgHistoryOpenIds.includes(lgId)) {
      setLgHistoryOpenIds(lgHistoryOpenIds.filter((id) => id !== lgId));
    } else {
      setLgHistoryOpenIds([...lgHistoryOpenIds, lgId]);
    }
  };

  return (
    <div>
      <div>
        <BodySmall>{t("invoiceAssessment.deliveryApproval")}</BodySmall>
        {lgLoading && <SmallLoadingSpinner />}
        {lgError && (
          <Alert
            type="error"
            title="Kunde inte hämta leveransgodkännanden"
            message={lgError?.message}
          />
        )}
        {(invoice?.recipientApprovals === undefined ||
          invoice?.recipientApprovals?.length === 0) && (
          <BodySmall color={theme.colors.textSecondary} textalign="right">
            {t("invoices.noDeliveryApprovalsAvailable")}
          </BodySmall>
        )}
        {invoice?.recipientApprovals?.map((lg) => (
          <div key={lg.id}>
            <Row
              justify="end"
              align="end"
              nogutter
              style={{ gap: 16, marginBottom: 8 }}
            >
              <BodySmall fontweight={500}>
                {t("invoiceAssessment.lgSentSuccess")}{" "}
                {dayjs(lg.sentAt).format("YYYY-MM-DD HH:mm")}
              </BodySmall>

              <Tooltip
                text={t("invoiceAssessment.markDeliveryApprovalAsDenied")}
              >
                <ToggleButton
                  iconOff={<CrossIcon color={theme.colors.primary} />}
                  iconOn={<CrossIcon color={theme.colors.white} />}
                  isOn={lg.status === "REJECTED"}
                  onToggle={() => {
                    setSelectedStatus(
                      lg.status === "REJECTED" ? "UNKNOWN" : "REJECTED",
                    );
                    setSelectedLgId(lg.id);
                    setCommentModalOpen(true);
                  }}
                  size="medium"
                  disabled={disabled}
                />
              </Tooltip>
              <Tooltip
                text={t("invoiceAssessment.markDeliveryApprovalAsApproved")}
              >
                <ToggleButton
                  iconOff={<CheckmarkIconSmall color={theme.colors.primary} />}
                  iconOn={<CheckmarkIconSmall color={theme.colors.white} />}
                  isOn={lg.status === "APPROVED"}
                  onToggle={() => {
                    setSelectedStatus(
                      lg.status === "APPROVED" ? "UNKNOWN" : "APPROVED",
                    );
                    setSelectedLgId(lg.id);
                    setCommentModalOpen(true);
                  }}
                  size="medium"
                  disabled={disabled}
                />
              </Tooltip>
            </Row>
            <Row nogutter justify="end">
              <DetailsButton
                title={t("invoiceAssessment.displayHistory")}
                onClick={() => onClickToggleLgHistory(lg.id)}
                icon={
                  lgHistoryOpenIds.includes(lg.id) ? (
                    <ChevronSmallUpIcon />
                  ) : (
                    <ChevronSmallDownIcon />
                  )
                }
              />
            </Row>
            <HorizontalSpacer spacing={1.5} />
            <SlideOutHeight isVisible={lgHistoryOpenIds.includes(lg.id)}>
              <LgHistoryDetailBlock lg={lg} />
            </SlideOutHeight>
          </div>
        ))}
      </div>

      {commentModalOpen && (
        <Modal
          title={t("invoiceAssessment.updateDeliverApprovalStatus")}
          onClose={() => setCommentModalOpen(false)}
          closeIcon
          width={600}
          actionButtons={{
            actionButton: {
              buttonTitle: "Uppdatera status",
              loading: loading,
              onClick: onUpdateStatus,
              disabled: success,
            },
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setCommentModalOpen(false),
            },
          }}
        >
          <div>
            <Body>
              {t("invoiceAssessment.updateDeliverApprovalStatusInformation")}
            </Body>
            <HorizontalSpacer spacing={0.5} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                justifyContent: "center",
              }}
            >
              <Tooltip
                text={t("invoiceAssessment.markDeliveryApprovalAsDenied")}
              >
                <ToggleButton
                  iconOff={<CrossIcon color={theme.colors.primary} />}
                  iconOn={<CrossIcon color={theme.colors.white} />}
                  isOn={selectedStatus === "REJECTED"}
                  onToggle={() =>
                    setSelectedStatus(
                      selectedStatus === "REJECTED" ? "UNKNOWN" : "REJECTED",
                    )
                  }
                  size="medium"
                />
              </Tooltip>
              <Tooltip
                text={t("invoiceAssessment.markDeliveryApprovalAsApproved")}
              >
                <ToggleButton
                  iconOff={<CheckmarkIconSmall color={theme.colors.primary} />}
                  iconOn={<CheckmarkIconSmall color={theme.colors.white} />}
                  isOn={selectedStatus === "APPROVED"}
                  onToggle={() =>
                    setSelectedStatus(
                      selectedStatus === "APPROVED" ? "UNKNOWN" : "APPROVED",
                    )
                  }
                  size="medium"
                />
              </Tooltip>
            </div>
            <HorizontalSpacer spacing={1} />
            <TextField
              label={t("common.comment")}
              optional
              placeholder={t("invoiceAssessment.updateLgCommentPlaceholder")}
              fullWidth
              value={comment}
              onChange={(value) => setComment(value)}
            />
            <HorizontalSpacer spacing={1} />
            {(errorMessage || error) && (
              <Alert
                type="error"
                title={t("errors.deliveryApprovalStatusCouldNotBeUpdated")}
                message={errorMessage || error?.message}
              />
            )}
            {success && (
              <Alert
                type="success"
                title={t("invoiceAssessment.deliveryApprovalStatusUpdated")}
              />
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DeliverApprovalStatusOnInvoice;
